import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { API, ELITE_PRICING, WORKOUT_ADDON, GST_RATE, loadRazorpayScript } from "./components/elite/constants";
import EliteHero from "./components/elite/EliteHero";
import EliteOverview from "./components/elite/EliteOverview";
import ElitePillars from "./components/elite/ElitePillars";
import EliteStructure from "./components/elite/EliteStructure";
import EliteCareTeam from "./components/elite/EliteCareTeam";
import ElitePricing from "./components/elite/ElitePricing";
import EnrollmentModal from "./components/elite/EnrollmentModal";

export default function FitlyElitePage() {
  const navigate = useNavigate();
  const [planType, setPlanType] = useState("individual");
  const [duration, setDuration] = useState("3 Months");
  const [hasWorkout, setHasWorkout] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [razorpayKey, setRazorpayKey] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    axios.get(`${API}/razorpay-key`).then((res) => { if (!cancelled && res.data.configured) setRazorpayKey(res.data.key_id); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const basePrice = ELITE_PRICING[duration]?.[planType] || 0;
  const addonPrice = hasWorkout ? WORKOUT_ADDON[planType] : 0;
  const subtotal = basePrice + addonPrice;
  const gst = Math.round(subtotal * GST_RATE);
  const total = subtotal + gst;

  const getProgramNameForBackend = useCallback(() => `Fitly Elite - ${duration} - ${planType === "couple" ? "Couple" : "Individual"}`, [duration, planType]);
  const getAddonNameForBackend = useCallback(() => hasWorkout ? `Workout - ${planType === "couple" ? "Couple" : "Individual"}` : null, [hasWorkout, planType]);
  const programLabel = `Fitly Elite — ${duration} (${planType === "couple" ? "Couple" : "Individual"})${hasWorkout ? " + Workout Sessions" : ""}`;

  const handlePayment = async (formData) => {
    if (!razorpayKey) return;
    setIsProcessing(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Failed to load payment gateway");
      const orderRes = await axios.post(`${API}/create-order`, {
        amount: subtotal * 100, program_name: getProgramNameForBackend(), add_on: getAddonNameForBackend(),
        customer_name: formData.name, customer_email: formData.email, customer_phone: formData.phone,
      });
      const options = {
        key: razorpayKey, amount: orderRes.data.amount, currency: orderRes.data.currency,
        name: "Fitly India", description: programLabel, order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/verify-payment`, { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature });
            setPaymentStatus({ type: "success", message: "Payment successful! Our team will contact you within 24 hours." });
          } catch { setPaymentStatus({ type: "error", message: "Payment verification failed. Contact support." }); }
        },
        prefill: { name: formData.name, email: formData.email, contact: formData.phone },
        theme: { color: "#0D1F17" },
        modal: { ondismiss: () => setIsProcessing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { setPaymentStatus({ type: "error", message: "Payment failed. Please try again." }); setIsProcessing(false); });
      rzp.open();
    } catch { setPaymentStatus({ type: "error", message: "Could not initiate payment. Please try again." }); }
    finally { setIsProcessing(false); }
  };

  return (
    <div className="min-h-screen bg-[#0D1F17] text-[#F7F5F0]">
      <header className="border-b border-[#F7F5F0]/5 bg-[#0D1F17]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A3B8AD] hover:text-[#F7F5F0] transition-colors" data-testid="back-to-home">
            <ArrowLeft size={18} /><span className="text-sm tracking-wide">Back</span>
          </button>
          <a href="/" className="font-serif text-xl">
            <span className="font-semibold text-[#F7F5F0]">Fitly</span> <span className="font-light text-[#A3B8AD]">India</span>
          </a>
        </div>
      </header>

      <EliteHero />
      <EliteOverview />
      <ElitePillars />
      <EliteStructure />
      <EliteCareTeam />
      <ElitePricing
        planType={planType} setPlanType={setPlanType}
        duration={duration} setDuration={setDuration}
        hasWorkout={hasWorkout} setHasWorkout={setHasWorkout}
        basePrice={basePrice} addonPrice={addonPrice}
        subtotal={subtotal} gst={gst} total={total}
        isProcessing={isProcessing}
        onEnroll={() => { setPaymentStatus(null); setShowModal(true); }}
      />

      <EnrollmentModal isOpen={showModal} onClose={() => { setShowModal(false); setPaymentStatus(null); }}
        onSubmit={handlePayment} programLabel={programLabel} subtotal={subtotal} gst={gst} total={total}
        isProcessing={isProcessing} paymentStatus={paymentStatus} />
    </div>
  );
}
